import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    token: localStorage.getItem("token")
  };
}

function GroupManagement({ userRole }) {
  const navigate = useNavigate();
  const [administeredGroups, setAdministeredGroups] = useState([]);
  const [memberGroups, setMemberGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: "", description: "" });
  const [ownerEmail, setOwnerEmail] = useState("");
  const [assignedRole, setAssignedRole] = useState(3);
  const [associations, setAssociations] = useState([]);
  useEffect(() => { if ([1,2].includes(userRole)) axios.get(`${API_URL}/associations`).then(r => setAssociations(r.data.associations)); }, [userRole]);
  const [inviteUsername, setInviteUsername] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [busyInvitationId, setBusyInvitationId] = useState("");

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/groups/my-groups`, { headers: authHeaders() });
      setAdministeredGroups(response.data.administeredGroups || []);
      setMemberGroups(response.data.memberGroups || []);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte hämta grupper");
    }
  }, []);

  const fetchGroupDetails = useCallback(async (groupId) => {
    if (!groupId) {
      setSelectedGroupDetails(null);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/groups/${groupId}`, { headers: authHeaders() });
      setSelectedGroupDetails(response.data);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte hämta gruppdetaljer");
    }
  }, []);

  async function transferGroup(event) {
    event.preventDefault();
    if (!window.confirm('Överlåta gruppansvaret? Väntande inbjudningar återkallas.')) return;
    try {
      const r = await axios.patch(`${API_URL}/groups/${selectedGroupId}/owner`, { email: ownerEmail }, { headers: authHeaders() });
      setStatusMessage(r.data.message); setOwnerEmail(''); setSelectedGroupId(''); setSelectedGroupDetails(null); await fetchGroups();
    } catch (e) { setErrorMessage(e.response?.data?.message || 'Gruppen kunde inte överlåtas.'); }
  }

  async function createGroup(event) {
    event.preventDefault();

    try {
      const response = await axios.post(`${API_URL}/groups/create`, newGroup, { headers: authHeaders() });
      setNewGroup({ name: "", description: "" });
      setSelectedGroupId(response.data._id);
      setStatusMessage("Gruppen skapades.");
      setErrorMessage("");
      await fetchGroups();
      await fetchGroupDetails(response.data._id);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte skapa gruppen");
      setStatusMessage("");
    }
  }

  async function inviteMember(event) {
    event.preventDefault();
    if (!selectedGroupId) {
      return;
    }

    try {
      await axios.post(`${API_URL}/groups/${selectedGroupId}/invite`, { email: inviteUsername, assignedRole }, { headers: authHeaders() });
      setInviteUsername("");
      setStatusMessage("Inbjudan skickades.");
      setErrorMessage("");
      await fetchGroupDetails(selectedGroupId);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte skicka inbjudan");
      setStatusMessage("");
    }
  }

  async function removeMember(username) {
    try {
      await axios.delete(`${API_URL}/groups/${selectedGroupId}/remove-member`, {
        headers: authHeaders(),
        data: { username }
      });
      setStatusMessage("Medlemmen togs bort.");
      setErrorMessage("");
      await fetchGroups();
      await fetchGroupDetails(selectedGroupId);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte ta bort medlemmen");
      setStatusMessage("");
    }
  }

  async function cancelInvitation(invitationId) {
    if (!window.confirm("Återkalla inbjudan? Mottagaren kan då inte längre acceptera den.")) {
      return;
    }
    try {
      setBusyInvitationId(invitationId);
      await axios.delete(`${API_URL}/groups/${selectedGroupId}/cancel-invite`, {
        headers: authHeaders(),
        data: { invitationId }
      });
      setStatusMessage("Inbjudan återkallades.");
      setErrorMessage("");
      await fetchGroupDetails(selectedGroupId);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte återkalla inbjudan");
      setStatusMessage("");
    } finally {
      setBusyInvitationId("");
    }
  }

  async function resendInvitation(invitationId) {
    try {
      setBusyInvitationId(invitationId);
      const response = await axios.post(
        `${API_URL}/groups/${selectedGroupId}/invitations/${invitationId}/resend`,
        {},
        { headers: authHeaders() }
      );
      setStatusMessage(response.data.message || "Inbjudan skickades om.");
      setErrorMessage("");
      await fetchGroupDetails(selectedGroupId);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte skicka om inbjudan");
      setStatusMessage("");
    } finally {
      setBusyInvitationId("");
    }
  }

  async function deleteGroup(groupId) {
    if (!window.confirm("Ta bort gruppen och alla gruppdelningar?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/groups/${groupId}`, { headers: authHeaders() });
      setSelectedGroupId("");
      setSelectedGroupDetails(null);
      setStatusMessage("Gruppen togs bort.");
      setErrorMessage("");
      await fetchGroups();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte ta bort gruppen");
      setStatusMessage("");
    }
  }

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchGroupDetails(selectedGroupId);
  }, [fetchGroupDetails, selectedGroupId]);

  const selectedGroup = selectedGroupDetails?.group;
  const pendingInvitations = selectedGroupDetails?.pendingInvitations || [];
  const expiredInvitations = selectedGroupDetails?.expiredInvitations || [];
  const sharedSessions = selectedGroupDetails?.sharedSessions || [];

  return (
    <div className="flex flex-col items-center px-3">
      <div className="w-full max-w-5xl py-4">
        <div className="theme-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Förening</p>
              <h1 className="mt-2 text-lg font-bold text-slate-800">Mina grupper</h1>
              <p className="mt-2 text-sm text-slate-600">
                Skapa grupper, bjud in medlemmar och hantera vilka som ska få tillgång till gruppens pass.
              </p>
            </div>
            <button
              className="rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100"
              onClick={() => navigate("/mypage")}
            >
              Till meny
            </button>
          </div>

          {statusMessage && <div className="theme-alert-success mt-4 py-3">{statusMessage}</div>}
          {errorMessage && <div className="theme-alert-error mt-4 py-3">{errorMessage}</div>}

          <form className="mt-5 grid gap-3 rounded-[22px] border border-sky-200/80 bg-white/85 p-4" onSubmit={createGroup}>
            {[1,2].includes(userRole) && <select aria-label="Förening" className="theme-input w-full" required value={newGroup.associationId || ""} onChange={e => setNewGroup({...newGroup, associationId:e.target.value})}><option value="">Välj förening</option>{associations.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}</select>}
            <p className="text-sm font-semibold text-slate-800">Skapa ny grupp</p>
            <input
              className="theme-input"
              value={newGroup.name}
              placeholder="Gruppnamn"
              onChange={(event) => setNewGroup({ ...newGroup, name: event.target.value })}
              required
            />
            <textarea
              className="theme-input min-h-[86px]"
              value={newGroup.description}
              placeholder="Beskrivning"
              onChange={(event) => setNewGroup({ ...newGroup, description: event.target.value })}
            />
            <button className="theme-button-primary w-full sm:w-56" type="submit">Skapa grupp</button>
          </form>

          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
            <div className="space-y-4">
              <section>
                <p className="mb-3 text-sm font-semibold text-slate-800">Grupper jag administrerar</p>
                <div className="space-y-3">
                  {administeredGroups.length === 0 && (
                    <div className="theme-empty-state">Du administrerar inga grupper ännu.</div>
                  )}
                  {administeredGroups.map((group) => (
                    <button
                      key={group._id}
                      className={`theme-panel w-full text-left ${selectedGroupId === group._id ? "border-sky-400 bg-sky-50" : ""}`}
                      onClick={() => setSelectedGroupId(group._id)}
                    >
                      <div className="font-semibold text-slate-800">{group.name}</div>
                      <div className="mt-2 text-sm text-slate-600">{group.members?.length || 0} medlemmar</div>
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <p className="mb-3 text-sm font-semibold text-slate-800">Grupper jag är medlem i</p>
                <div className="space-y-3">
                  {memberGroups.length === 0 && (
                    <div className="theme-empty-state">Du är inte medlem i någon grupp ännu.</div>
                  )}
                  {memberGroups.map((group) => (
                    <div key={group._id} className="theme-panel">
                      <div className="font-semibold text-slate-800">{group.name}</div>
                      <div className="mt-2 text-sm text-slate-600">Administratör: {group.administratorName || "Okänd administratör"}</div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <section className="theme-panel">
              {!selectedGroup && (
                <div className="theme-empty-state">Välj en grupp för att bjuda in och hantera medlemmar.</div>
              )}

              {selectedGroup && (
                <div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">{selectedGroup.name}</h2>
                      <p className="mt-2 text-sm text-slate-600">{selectedGroup.description || "Ingen beskrivning"}</p>
                    </div>
                    <button
                      className="theme-badge-button border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => deleteGroup(selectedGroup._id)}
                    >
                      Ta bort grupp
                    </button>
                  </div>

                  <form className="mt-5 flex flex-col gap-3 sm:flex-row" onSubmit={inviteMember}>
                    <input
                      className="theme-input flex-1"
                      value={inviteUsername}
                      placeholder="E-postadress" type="email"
                      onChange={(event) => setInviteUsername(event.target.value)}
                      required
                    />
                    {[1,4].includes(userRole) && <select aria-label="Roll i inbjudan" className="theme-input" value={assignedRole} onChange={e => setAssignedRole(Number(e.target.value))}><option value={3}>Utövare</option><option value={5}>Tränare</option></select>}
                    <button className="theme-button-primary sm:w-40" type="submit">Bjud in</button>
                  </form>

                  <details className="mt-4"><summary>Överlåt gruppansvar</summary><p className="my-2">Återkalla gruppens passdelningar först. Ny ansvarig måste vara Föreningsadmin eller Tränare i samma förening.</p><form onSubmit={transferGroup} className="flex flex-wrap gap-2"><input aria-label="Ny gruppansvarigs e-postadress" type="email" required className="theme-input" value={ownerEmail} onChange={e=>setOwnerEmail(e.target.value)} placeholder="Ny ansvarigs e-postadress"/><button className="theme-button-secondary">Överlåt</button></form></details>
                  <div className="mt-5">
                    <p className="text-sm font-semibold text-slate-800">Medlemmar</p>
                    <div className="mt-3 space-y-2">
                      {(selectedGroup.members || []).map((member) => (
                        <div key={member} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                          <span className="break-all text-sm font-medium text-slate-700">{selectedGroupDetails.memberProfiles?.find(p => p.username === member)?.name || selectedGroupDetails.memberProfiles?.find(p => p.username === member)?.email || 'Okänd användare'}</span>
                          {member !== selectedGroup.administratorUsername && (
                            <button
                              className="theme-badge-button border-red-200 text-red-700 hover:bg-red-50"
                              onClick={() => removeMember(member)}
                            >
                              Ta bort
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <p className="text-sm font-semibold text-slate-800">Väntande inbjudningar</p>
                    <div className="mt-3 space-y-2">
                      {pendingInvitations.length === 0 && (
                        <div className="theme-empty-state">Inga väntande inbjudningar.</div>
                      )}
                      {pendingInvitations.map((invitation) => (
                        <div key={invitation._id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <span className="break-all text-sm font-medium text-slate-700">{invitation.invitedName || "Inbjuden användare"} · {invitation.assignedRole === 5 ? "Tränare" : "Utövare"}</span>
                              <p className="mt-1 text-xs text-slate-500">Väntar · gäller till {new Date(invitation.expiresAt).toLocaleDateString("sv-SE")}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:flex">
                              <button
                                type="button"
                                disabled={busyInvitationId === invitation._id}
                                className="theme-badge-button min-h-[44px] border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-60"
                                onClick={() => resendInvitation(invitation._id)}
                              >
                                Skicka om
                              </button>
                              <button
                                type="button"
                                disabled={busyInvitationId === invitation._id}
                                className="theme-badge-button min-h-[44px] border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => cancelInvitation(invitation._id)}
                              >
                                Återkalla
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <p className="text-sm font-semibold text-slate-800">Utgångna inbjudningar</p>
                    <div className="mt-3 space-y-2">
                      {expiredInvitations.length === 0 && (
                        <div className="theme-empty-state">Inga utgångna inbjudningar.</div>
                      )}
                      {expiredInvitations.map((invitation) => (
                        <div key={invitation._id} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <span className="break-all text-sm font-medium text-slate-700">{invitation.invitedName || "Inbjuden användare"} · {invitation.assignedRole === 5 ? "Tränare" : "Utövare"}</span>
                              <p className="mt-1 text-xs font-medium text-amber-700">Gick ut {new Date(invitation.expiresAt).toLocaleDateString("sv-SE")}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:flex">
                              <button
                                type="button"
                                disabled={busyInvitationId === invitation._id}
                                className="theme-badge-button min-h-[44px] border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-60"
                                onClick={() => resendInvitation(invitation._id)}
                              >
                                Skicka om
                              </button>
                              <button
                                type="button"
                                disabled={busyInvitationId === invitation._id}
                                className="theme-badge-button min-h-[44px] border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => cancelInvitation(invitation._id)}
                              >
                                Återkalla
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-800">Pass delade med gruppen</p>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">{sharedSessions.length}</span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {sharedSessions.length === 0 && (
                        <div className="theme-empty-state">Inga pass är delade med gruppen.</div>
                      )}
                      {sharedSessions.map((share) => (
                        <article key={share.shareId} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800">{share.session.title || "Namnlöst pass"}</p>
                              {share.session.description && <p className="mt-1 text-sm text-slate-600">{share.session.description}</p>}
                              <p className="mt-2 text-xs text-slate-500">
                                {share.session.stationCount} stationer · Delat av {share.sharedByName || "Okänd användare"} {new Date(share.sharedAt).toLocaleDateString("sv-SE")}
                              </p>
                              {share.session.archivedAt && <p className="mt-1 text-xs font-semibold text-amber-700">Arkiverat pass</p>}
                            </div>
                            <button
                              type="button"
                              className="theme-badge-button min-h-[44px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => navigate("/run-session", { state: { session: share.session._id } })}
                            >
                              Kör pass
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GroupManagement;
